import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Receipt,
  ShieldCheck,
  Download,
  Zap,
  Rocket,
  User,
  Loader2,
  Calendar,
  AlertTriangle,
  HeartOff,
  Gift,
  ShieldAlert,
  ChevronRight,
  MessageCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useSupport } from '../../contexts/SupportContext';
import axios from 'axios';
import moment from 'moment';
import { API_BASE_URL } from '../../config';
import { brand } from '../../config/brand';
import { toast } from 'sonner';

interface Order {
  id: string;
  polarOrderId?: string;
  invoiceNumber?: string;
  variantId: string;
  tier: string;
  amount: number;
  taxAmount?: number;
  subtotalAmount?: number;
  currency: string;
  status: string;
  createdAt: string;
}

export default function Billing(): JSX.Element {
  const { user, setUser } = useGlobalState();
  const { openSupport } = useSupport();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [purgeData, setPurgeData] = useState(false);
  const [applyingFreeMonth, setApplyingFreeMonth] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [confirmUpdateModalOpen, setConfirmUpdateModalOpen] = useState(false);
  const [updateModalData, setUpdateModalData] = useState<{ message: string, checkoutUrl: string } | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [pendingTargetTier, setPendingTargetTier] = useState<string | null>(null);

  const token = localStorage.getItem('token');

  const fetchOrders = React.useCallback(async () => {
    try {
      setLoadingOrders(true);
      const response = await axios.get(`${API_BASE_URL}/subscriptions/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Fetched orders:", response.data);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchOrders();
    }
  }, [token, fetchOrders]);

  const lastOrder = orders.length > 0 ? orders[0] : null;
  const isYearly = lastOrder && (
    (lastOrder.variantId && (
      lastOrder.variantId === 'aea684fb-ad3f-48f6-9197-96a45ae8f36b' || // Basic Yearly
      lastOrder.variantId === '1f7f64ed-e1b9-4221-9445-a53981753b9e' || // Indie Yearly
      lastOrder.variantId === '53c87aed-d24a-47c2-bc84-01f4f4d426cc' || // Prod Basic Yearly
      lastOrder.variantId === '58defef8-a583-4a32-b1c6-9f378f94a50e'    // Prod Indie Yearly
    )) || 
    (lastOrder.amount > 10000) // Rough guess: if it's more than $100, it's likely yearly
  );

  // Fixed tier prices (in dollars)
  const TIER_PRICES = {
    BASIC: { monthly: 4, yearly: 36 },
    PRO: { monthly: 20, yearly: 192 },
    FREE: { monthly: 0, yearly: 0 }
  };

  const getCurrentTierPrice = () => {
    if (!user?.tier || user.tier === 'FREE') return 0;
    const tierKey = user.tier as 'BASIC' | 'PRO';
    return isYearly ? TIER_PRICES[tierKey].yearly : TIER_PRICES[tierKey].monthly;
  };

  const handleDownloadInvoice = async (orderId: string, orderNumber: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/subscriptions/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice.");
    }
  };

  const handleCancelClick = () => {
    setCancelStep(1);
    setCancelModalOpen(true);
  };

  const handleApplyFreeMonth = async () => {
    setApplyingFreeMonth(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/subscriptions/apply-free-month`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);

      // Refresh user data
      const userRes = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data.user);

      setCancelModalOpen(false);
    } catch (error: any) {
      console.error("Free month error:", error);
      toast.error(error.response?.data?.error || "Failed to apply offer.");
    } finally {
      setApplyingFreeMonth(false);
    }
  };

  const handleUpdatePlan = async (targetTier: string) => {
    if (!token) return;
    
    // If downgrading (from PRO to BASIC), show confirmation modal first
    if (user?.tier === 'PRO' && targetTier === 'BASIC') {
      setPendingTargetTier(targetTier);
      setDowngradeModalOpen(true);
      return;
    }

    // If upgrading (from BASIC to PRO/INDIE), show confirmation modal first
    if (user?.tier === 'BASIC' && (targetTier === 'PRO' || targetTier === 'INDIE')) {
      setPendingTargetTier(targetTier);
      setUpgradeModalOpen(true);
      return;
    }

    // Proceed with update for other cases
    await executeUpdatePlan(targetTier);
  };

  const executeUpdatePlan = async (targetTier: string) => {
    if (!token) return;
    setUpdatingPlan(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/subscriptions/update-plan`, { 
        targetTier
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.checkoutUrl) {
        setUpdateModalData({
          message: response.data.message,
          checkoutUrl: response.data.checkoutUrl
        });
        setConfirmUpdateModalOpen(true);
        return;
      }
      toast.success(response.data.message);

      // Refresh user data after a small delay
      setTimeout(async () => {
        const userRes = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data.user);
      }, 3000);
    } catch (error: any) {
      console.error("Update plan error:", error);
      const errorData = error.response?.data;
      const detailMsg = errorData?.details ? (Array.isArray(errorData.details) ? ` - ${errorData.details[0].detail}` : ` - ${JSON.stringify(errorData.details)}`) : '';
      toast.error(`${errorData?.error || "Failed to update plan."}${detailMsg}`);
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleConfirmDowngrade = async () => {
    setDowngradeModalOpen(false);
    if (pendingTargetTier) {
      await executeUpdatePlan(pendingTargetTier);
      setPendingTargetTier(null);
    }
  };

  const handleConfirmUpgrade = async () => {
    setUpgradeModalOpen(false);
    if (pendingTargetTier) {
      await executeUpdatePlan(pendingTargetTier);
      setPendingTargetTier(null);
    }
  };

  const calculateProratedUpgradeAmount = () => {
    if (!user?.subscriptionEndsAt) return 0;
    
    const now = new Date();
    const endsAt = new Date(user.subscriptionEndsAt);
    const totalPeriodMs = isYearly ? 365 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
    const remainingMs = endsAt.getTime() - now.getTime();
    const remainingRatio = remainingMs / totalPeriodMs;
    
    const currentPrice = user.tier === 'BASIC' ? (isYearly ? 36 : 4) : 0;
    const targetPrice = isYearly ? 192 : 20;
    
    const remainingCurrent = currentPrice * remainingRatio;
    const remainingTarget = targetPrice * remainingRatio;
    
    return Math.max(0, Math.ceil(remainingTarget - remainingCurrent));
  };

  const handleFinalCancel = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/subscriptions/cancel`, {
        feedback: cancelFeedback,
        purgeOnExpiry: purgeData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);

      // Wait a brief moment to ensure backend has fully processed the cancellation
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh user data
      const userRes = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userRes.data.user);
      setCancelStep(4);
    } catch (error: any) {
      console.error("Cancel error:", error);
      toast.error(error.response?.data?.error || "Failed to cancel subscription.");
    } finally {
      setCancelling(false);
    }
  };

  const getTierDetails = (tier?: string) => {
    switch (tier) {
      case 'PRO':
        return { name: brand.planNames.pro.toUpperCase(), icon: <Zap className="h-4 w-4 text-primary" />, color: 'bg-primary' };
      case 'BASIC':
        return { name: brand.planNames.basic.toUpperCase(), icon: <Rocket className="h-4 w-4 text-indigo-400" />, color: 'bg-indigo-600' };
      default:
        return { name: 'FREE', icon: <User className="h-4 w-4 text-muted-foreground" />, color: 'bg-slate-600' };
    }
  };

  const tierDetails = getTierDetails(user?.tier);
  const isSubscriber = user?.tier !== 'FREE';
  const isCancelled = user?.subscriptionStatus === 'cancelled' || user?.subscriptionStatus === 'expired';
  // Check if user has a manual subscription (assigned by admin)
  const isManualPlan = (user as any)?.isManualSubscription === true;
  const hasActiveSubscription = !isManualPlan && !!user?.polarSubscriptionId;

  return (
    <div className="container mx-auto py-10 px-4 max-w-4xl space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <CreditCard className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">Subscription & Orders</h1>
            <p className="text-muted-foreground font-medium">Manage your plan, billing history, and orders.</p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-[2.5rem] p-10 shadow-2xl relative overflow-hidden group">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
          <div className="md:col-span-2 space-y-8">
            <div className="bg-background p-8 rounded-[2rem] border border-border shadow-inner space-y-6">
              <div className="flex items-center justify-between">
                <Badge className={cn("text-[#0f172a] font-black uppercase text-[10px] tracking-[0.2em] px-3 border-none", tierDetails.color)}>
                  {tierDetails.name} Tier
                </Badge>
                {isCancelled && user?.subscriptionEndsAt && user?.tier !== 'FREE' && (
                  <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Ends on {moment(user.subscriptionEndsAt).format('MMM D, YYYY')}
                  </span>
                )}
                {!isCancelled && isSubscriber && (
                  <div className="flex flex-col items-end">
                    <span className="text-[10px] font-black text-green-500 uppercase tracking-widest flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" />
                      Active Plan
                    </span>
                    {user?.subscriptionEndsAt && user?.tier !== 'FREE' && (
                      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-tighter mt-0.5">
                        {user?.polarSubscriptionId
                          ? `Renews on ${moment(user.subscriptionEndsAt).format('MMM D, YYYY')}`
                          : `Ends on ${moment(user.subscriptionEndsAt).format('MMM D, YYYY')}`
                        }
                      </span>
                    )}
                  </div>
                )}
              </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-5xl font-black text-foreground leading-none">
                      {user?.tier === 'FREE' ? (
                        '$0'
                      ) : isManualPlan ? (
                        null
                      ) : isCancelled ? (
                        <span className="text-3xl text-muted-foreground">Cancelled</span>
                      ) : hasActiveSubscription ? (
                        `$${getCurrentTierPrice()}`
                      ) : (
                        <span className="text-3xl text-muted-foreground">No Active Subscription</span>
                      )}
                      {hasActiveSubscription && user?.tier !== 'FREE' && !isCancelled && (
                        <span className="text-lg text-muted-foreground font-medium ml-2">/{isYearly ? 'year' : 'month'}</span>
                      )}
                    </h2>
                  </div>
                <p className="text-sm text-muted-foreground font-medium italic">
                  {user?.tier === 'PRO' ? 'High-performance tools for serious creators.' :
                    user?.tier === 'BASIC' ? 'Propelling your first game into the market.' :
                      'Only for read only access and removing old content'}
                </p>
                {isManualPlan && user?.tier !== 'FREE' && (
                  <p className="text-xs text-orange-400 font-bold mt-2">
                    This plan was assigned manually and will not renew automatically.
                  </p>
                )}
                {isCancelled && !isManualPlan && user?.subscriptionEndsAt && (
                  <p className="text-xs text-orange-400 font-bold mt-2">
                    Your subscription has been cancelled. You will retain access until {moment(user.subscriptionEndsAt).format('MMMM D, YYYY')}.
                  </p>
                )}
              </div>

              {user?.tier !== 'FREE' && <div className="h-px bg-card w-full" />}

              <ul className="space-y-3">
                {(user?.tier === 'PRO' ?
                  ['5 Active Projects', '8 Social Connections', 'AI Content Assistant', 'Advanced Analytics', 'Permanent Video Storage'] :
                  user?.tier === 'BASIC' ?
                    ['1 Active Project', '2 Social Connections', 'Multi-platform Scheduling', 'Daily Content Tasks'] :
                    []
                ).map((feat, i) => (
                  <li key={i} className="flex items-center gap-3 text-foreground font-medium text-sm">
                    <ShieldCheck className="h-4 w-4 text-green-500" />
                    {feat}
                  </li>
                ))}
              </ul>

              {hasActiveSubscription && !isCancelled && user?.tier !== 'FREE' && (
                <div className="pt-4 flex items-center justify-between border-t border-border/50 mt-4">
                  {user?.tier === 'BASIC' ? (
                    <Button
                      variant="outline"
                      className="border-primary/30 text-primary hover:bg-primary/10 text-[10px] font-black uppercase tracking-widest h-8"
                      onClick={() => handleUpdatePlan('INDIE')}
                      disabled={updatingPlan}
                    >
                      {updatingPlan ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Zap className="h-3 w-3 mr-2" />}
                      Upgrade to {brand.planNames.pro}
                    </Button>
                  ) : user?.tier === 'PRO' ? (
                    <Button
                      variant="outline"
                      className="border-border text-muted-foreground hover:text-foreground hover:bg-muted/30 text-[10px] font-black uppercase tracking-widest h-8"
                      onClick={() => handleUpdatePlan('BASIC')}
                      disabled={updatingPlan}
                    >
                      {updatingPlan ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <ArrowRight className="h-3 w-3 rotate-180 mr-2" />}
                      Downgrade to Basic
                    </Button>
                  ) : null}

                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-red-400 text-[10px] font-black uppercase tracking-widest h-8"
                    onClick={handleCancelClick}
                  >
                    Cancel Subscription
                  </Button>
                </div>
              )}
              {isManualPlan && !isCancelled && user?.tier !== 'FREE' && (
                <div className="pt-4 border-t border-border/50 mt-4">
                  <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
                    <p className="text-xs text-orange-400 font-medium">
                      <strong className="font-black">Manual Plan:</strong> This plan was assigned by an administrator and does not have automatic renewal. If you need to extend or modify your access, please contact support.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-black text-foreground flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Order History
              </h3>
            </div>

            {loadingOrders ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-background rounded-2xl border border-border p-8 text-center border-dashed">
                <p className="text-muted-foreground text-xs italic">No transactions found in your history.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <div key={order.id} className="bg-background p-5 rounded-2xl border border-border flex items-center justify-between group/order hover:border-border transition-colors">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-muted-foreground uppercase tracking-widest">#{order.invoiceNumber || order.polarOrderId || order.id}</span>
                        <Badge variant="outline" className="text-[8px] h-4 py-0 border-border text-muted-foreground">{order.status.toUpperCase()}</Badge>
                      </div>
                      <div className="text-sm font-bold text-foreground uppercase tracking-tight">
                        {order.tier === 'PRO' ? brand.planNames.pro.toUpperCase() : order.tier} Plan - {moment(order.createdAt).format('MMMM D, YYYY')}
                      </div>
                      <div className="text-xs font-medium text-muted-foreground italic">
                        {order.taxAmount && order.taxAmount > 0 ? (
                          <>
                            Subtotal: ${(order.subtotalAmount! / 100).toFixed(2)} + VAT: ${(order.taxAmount / 100).toFixed(2)} = 
                            <span className="text-foreground ml-1">Total: ${(order.amount / 100).toFixed(2)} {order.currency}</span>
                          </>
                        ) : (
                          `Total: $${(order.amount / 100).toFixed(2)} ${order.currency}`
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10"
                      onClick={() => handleDownloadInvoice(order.id, (order.invoiceNumber || order.polarOrderId || order.id)!)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            {(!isSubscriber || isCancelled) && (
              <div className="bg-primary/5 border border-primary/20 rounded-3xl p-6 space-y-4 relative overflow-hidden group/upgrade">
                <div className="absolute -right-4 -top-4 text-primary/10 group-hover/upgrade:text-primary/20 transition-colors rotate-12">
                  <Zap size={80} />
                </div>
                <h4 className="text-sm font-black text-primary uppercase tracking-widest italic relative z-10">
                  {isCancelled ? 'Renew Subscription' : 'Upgrade Account'}
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed font-medium relative z-10">
                  {isCancelled 
                    ? 'Your subscription will end soon. Renew now to keep access to all premium features.'
                    : 'Unlock unlimited projects, bulk scheduling, and advanced AI features to dominate the market.'
                  }
                </p>
                <Button
                  onClick={() => window.location.href = '/pricing'}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-black h-12 rounded-xl shadow-lg shadow-primary/20 active:scale-[0.98] transition-all relative z-10">
                  VIEW PLANS
                </Button>
              </div>
            )}

            <div className="bg-background border border-border rounded-3xl p-6 space-y-4">
              <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Contact Us</h4>
              <Button
                variant="outline"
                onClick={() => openSupport()}
                className="w-full border-border bg-transparent text-foreground hover:bg-card font-bold h-10 text-xs rounded-xl"
              >
                <MessageCircle className="h-3 w-3 mr-2" />
                SUPPORT CENTER
              </Button>
            </div>

            <p className="text-[10px] text-muted-foreground text-center leading-relaxed px-4 font-medium uppercase tracking-tight">
              Secure payments powered by Polar.sh. <br />Cancel anytime with one click.
            </p>
          </div>
        </div>
      </div>

      {/* Cancellation Multi-step Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md bg-card border-border rounded-[2.5rem] p-0 overflow-hidden outline-none">
          <div className="p-8 space-y-6">
            {cancelStep === 1 && (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                <div className="bg-orange-500/10 w-16 h-16 rounded-2xl flex items-center justify-center border border-orange-500/20 shadow-lg shadow-orange-500/5">
                  <HeartOff className="h-8 w-8 text-orange-500" />
                </div>
                <div>
                  <DialogTitle className="text-3xl font-black text-foreground italic">WE'RE SORRY TO SEE YOU GO</DialogTitle>
                  <DialogDescription className="text-muted-foreground font-medium mt-2">
                    Is there anything we could do better? Your feedback helps us improve for the whole community.
                  </DialogDescription>
                </div>

                <Textarea
                  placeholder="What could we improve?"
                  value={cancelFeedback}
                  onChange={(e) => setCancelFeedback(e.target.value)}
                  className="bg-background border-border rounded-2xl min-h-[100px] resize-none focus-visible:ring-primary"
                />

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="secondary"
                    className="bg-card hover:bg-muted text-foreground font-bold rounded-xl"
                    onClick={() => {
                      setCancelModalOpen(false);
                      openSupport('General support');
                    }}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    CONTACT SUPPORT
                  </Button>
                  <Button
                    className="bg-primary hover:bg-primary/90 text-white font-bold rounded-xl"
                    onClick={() => {
                      const oneMonthAgo = moment().subtract(1, 'month');
                      // orders are DESC, so the last one in the list is the oldest (first order)
                      const firstOrder = orders.length > 0 ? orders[orders.length - 1] : null;
                      const hasBeenSubscriberLongEnough = firstOrder && moment(firstOrder.createdAt).isBefore(oneMonthAgo);

                      if (hasBeenSubscriberLongEnough && !user?.usedFreeMonthOffer) {
                        setCancelStep(2);
                      } else {
                        setCancelStep(3);
                      }
                    }}
                  >
                    CONTINUE
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {cancelStep === 2 && (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300 text-center py-4">
                <div className="bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/5 mx-auto">
                  <Gift className="h-10 w-10 text-primary animate-bounce" />
                </div>
                <div className="space-y-2 text-center">
                  <h2 className="text-3xl font-black text-foreground italic">WAIT! HERE IS A GIFT</h2>
                  <p className="text-muted-foreground font-medium">
                    We value your work as a creator. Stay with us one more month, on the house!
                  </p>
                </div>

                <div className="bg-background p-4 rounded-2xl border border-primary/20">
                  <div className="text-[10px] font-black text-primary uppercase tracking-widest mb-1">Exclusive Offer</div>
                  <div className="text-2xl font-black text-foreground">1 FREE MONTH</div>
                  <div className="text-xs text-muted-foreground">Upgrade your visibility for 30 more days at no cost.</div>
                </div>

                <div className="space-y-3 pt-4">
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-white font-black h-12 rounded-xl"
                    onClick={handleApplyFreeMonth}
                    disabled={applyingFreeMonth}
                  >
                    {applyingFreeMonth ? <Loader2 className="animate-spin h-5 w-5" /> : "YES, I WANT THE FREE MONTH!"}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-foreground font-bold"
                    onClick={() => setCancelStep(3)}
                  >
                    Thanks, but I want to cancel
                  </Button>
                </div>
              </div>
            )}

            {cancelStep === 3 && (
              <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                <div className="bg-red-500/10 w-16 h-16 rounded-2xl flex items-center justify-center border border-red-500/20 shadow-lg shadow-red-500/5">
                  <ShieldAlert className="h-8 w-8 text-red-500" />
                </div>
                <div>
                  <DialogTitle className="text-3xl font-black text-red-500 italic uppercase">Final Confirmation</DialogTitle>
                  <DialogDescription className="text-muted-foreground font-medium mt-2">
                    {isManualPlan ? (
                      <>Your plan will be <strong className="text-red-400">immediately downgraded to Free</strong> upon confirmation.</>
                    ) : (
                      <>Once cancelled, your plan will be downgraded to Free on <strong>{user?.subscriptionEndsAt ? moment(user.subscriptionEndsAt).format('MMMM D, YYYY') : 'the end of your billing cycle'}</strong>.</>
                    )}
                  </DialogDescription>
                </div>

                {!isManualPlan && (
                  <div className="bg-background p-6 rounded-[1.5rem] border border-border space-y-4">
                    <div className="flex items-start space-x-3 group cursor-pointer" onClick={() => setPurgeData(!purgeData)}>
                      <div className="pt-0.5">
                        <Checkbox checked={purgeData} onCheckedChange={(val) => setPurgeData(!!val)} className="border-border data-[state=checked]:bg-red-600" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-sm font-bold text-foreground cursor-pointer">GDPR: Purge social credentials</label>
                        <p className="text-xs text-muted-foreground leading-normal italic">
                          Automatically delete all social access tokens and processed videos when the subscription ends.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  variant="destructive"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-black h-14 rounded-2xl shadow-lg shadow-red-500/20 active:scale-[0.98] transition-all"
                  onClick={handleFinalCancel}
                  disabled={cancelling}
                >
                  {cancelling ? <Loader2 className="animate-spin h-5 w-5" /> : isManualPlan ? "END PLAN NOW" : "CONFIRM CANCELLATION"}
                </Button>
              </div>
            )}

            {cancelStep === 4 && (
              <div className="py-12 text-center space-y-6 animate-in zoom-in duration-300">
                <div className="bg-card w-20 h-20 rounded-full flex items-center justify-center mx-auto border border-border shadow-xl">
                  <Calendar className="h-10 w-10 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-3xl font-black text-foreground italic uppercase">
                    {isManualPlan ? "PLAN ENDED" : "SUBSCRIPTION CANCELLED"}
                  </h2>
                  <p className="text-muted-foreground font-medium max-w-xs mx-auto leading-relaxed">
                    {isManualPlan ? (
                      <>Your plan has been ended and you have been downgraded to <span className="text-red-400 font-bold">FREE</span>.</>
                    ) : (
                      <>We're sorry to see you go! You will continue to have access to your plan until <span className="text-primary font-bold">{moment(user?.subscriptionEndsAt).format('MMMM D, YYYY')}</span>.</>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setCancelModalOpen(false);
                    // Force a page reload to ensure all data is fresh
                    window.location.reload();
                  }}
                  className="bg-card hover:bg-muted text-foreground font-black px-8 h-12 rounded-xl"
                >
                  GOT IT
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Downgrade Confirmation Modal */}
      <Dialog open={downgradeModalOpen} onOpenChange={setDowngradeModalOpen}>
        <DialogContent className="max-w-md bg-card border-border rounded-[2.5rem] p-0 overflow-hidden outline-none">
          <div className="p-8 space-y-6">
            <div className="bg-orange-500/10 w-16 h-16 rounded-2xl flex items-center justify-center border border-orange-500/20 shadow-lg shadow-orange-500/5">
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
            <div>
              <DialogTitle className="text-3xl font-black text-foreground italic uppercase">
                Confirm Downgrade
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-medium mt-4 leading-relaxed">
                You're about to downgrade from <span className="text-primary font-bold">Indie</span> to <span className="text-indigo-400 font-bold">Basic</span>.
                <br /><br />
                <span className="text-green-400 font-bold">What will happen:</span>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>A prorated refund will be issued for the unused time on your current plan</li>
                  <li>The change will take effect at the end of your current billing period</li>
                  <li>Your projects and data will be preserved</li>
                  <li>Some features will be limited to Basic tier limits</li>
                </ul>
                <br />
                Are you sure you want to continue?
              </DialogDescription>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1 bg-card hover:bg-muted text-foreground font-bold rounded-xl"
                onClick={() => {
                  setDowngradeModalOpen(false);
                  setPendingTargetTier(null);
                }}
              >
                CANCEL
              </Button>
              <Button
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl"
                onClick={handleConfirmDowngrade}
                disabled={updatingPlan}
              >
                {updatingPlan ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                YES, DOWNGRADE
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Confirmation Modal */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="max-w-md bg-card border-border rounded-[2.5rem] p-0 overflow-hidden outline-none">
          <div className="p-8 space-y-6">
            <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/5">
              <Zap className="h-8 w-8 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-3xl font-black text-foreground italic uppercase">
                Confirm Upgrade
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-medium mt-4 leading-relaxed">
                You're about to upgrade from <span className="text-indigo-400 font-bold">Basic</span> to <span className="text-primary font-bold">Indie</span>!
                <br /><br />
                <span className="text-green-400 font-bold">What will happen:</span>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>You'll be charged approximately <span className="text-foreground font-bold">${calculateProratedUpgradeAmount()}</span> today (prorated difference)</li>
                  <li>Your upgrade will be processed <span className="text-foreground font-bold">immediately</span></li>
                  <li>You'll get instant access to all Indie features</li>
                  <li>Your next billing date remains <span className="text-foreground font-bold">{user?.subscriptionEndsAt ? moment(user.subscriptionEndsAt).format('MMM D, YYYY') : 'unchanged'}</span></li>
                  <li>At renewal, you'll pay the full Indie price (${isYearly ? '192/year' : '20/month'})</li>
                </ul>
                <br />
                Ready to level up?
              </DialogDescription>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1 bg-card hover:bg-muted text-foreground font-bold rounded-xl"
                onClick={() => {
                  setUpgradeModalOpen(false);
                  setPendingTargetTier(null);
                }}
              >
                CANCEL
              </Button>
              <Button
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl"
                onClick={handleConfirmUpgrade}
                disabled={updatingPlan}
              >
                {updatingPlan ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                YES, UPGRADE!
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal for Plan Update */}
      <Dialog open={confirmUpdateModalOpen} onOpenChange={(open) => {
        setConfirmUpdateModalOpen(open);
        if (!open) setIsRedirecting(false);
      }}>
        <DialogContent className="max-w-md bg-card border-border rounded-[2.5rem] p-0 overflow-hidden outline-none">
          <div className="p-8 space-y-6">
            <div className="bg-primary/10 w-16 h-16 rounded-2xl flex items-center justify-center border border-primary/20 shadow-lg shadow-primary/5">
              {isRedirecting ? <Loader2 className="h-8 w-8 text-primary animate-spin" /> : <ShieldCheck className="h-8 w-8 text-primary" />}
            </div>
            <div>
              <DialogTitle className="text-3xl font-black text-foreground italic uppercase">
                {isRedirecting ? "Redirecting..." : "Confirm Change"}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground font-medium mt-2 min-h-[60px] flex items-center justify-center text-center">
                {isRedirecting ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-6 w-6 text-primary animate-spin" />
                    <span>Preparing your secure payment page...</span>
                  </div>
                ) : (
                  updateModalData?.message
                )}
              </DialogDescription>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1 bg-card hover:bg-muted text-foreground font-bold rounded-xl"
                onClick={() => setConfirmUpdateModalOpen(false)}
                disabled={isRedirecting}
              >
                CANCEL
              </Button>
              <Button
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl"
                disabled={isRedirecting}
                onClick={() => {
                  if (updateModalData?.checkoutUrl) {
                    setIsRedirecting(true);
                    window.location.href = updateModalData.checkoutUrl;
                  }
                }}
              >
                {isRedirecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                PROCEED
                {!isRedirecting && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
